import type { ComputedRef, MaybeRefOrGetter, Ref, ShallowRef } from 'vue';
import type { WaveSurferEvents, WaveSurferOptions } from 'wavesurfer.js';
import { computed, reactive, shallowRef, toValue, watchEffect } from 'vue';
/**
 * A Vue composable for wavesurfer.js
 *
 * Usage:
 *
 * import { useWavesurfer } from './use-wavesurfer'
 *
 * <script setup>
 * const containerRef = useTemplateRef('container')
 * const { wavesurfer, isReady, isPlaying, currentTime } = useWavesurfer({
 *   container: containerRef,
 *   url: '/my-server/audio.ogg',
 *   waveColor: 'purple',
 *   height: 100,
 *   onReady: (wavesurfer) => console.log('Ready!', wavesurfer)
 * })
 * </script>
 *
 * <template>
 *   <div ref="container" />
 * </template>
 */
import WaveSurfer from 'wavesurfer.js';
import { mapValues } from '#layers/base/public';

type WavesurferEventHandler<T extends unknown[]> = (wavesurfer: WaveSurfer, ...args: T) => void;

type OnWavesurferEvents = {
	[K in keyof WaveSurferEvents as `on${Capitalize<K>}`]?: WavesurferEventHandler<WaveSurferEvents[K]>
};

type DeepMaybeRefOrGetterWavesurferOptions = {
	[K in keyof Omit<WaveSurferOptions, 'container' | 'media'>]: MaybeRefOrGetter<WaveSurferOptions[K]>
} & {
	media?: MaybeRefOrGetter<Exclude<WaveSurferOptions['media'], undefined> | null>;
	container: MaybeRefOrGetter<HTMLDivElement | null>;
};

/**
 * Props for the Wavesurfer composable
 * @public
 */
export type WavesurferProps = DeepMaybeRefOrGetterWavesurferOptions & OnWavesurferEvents;

/**
 * Use wavesurfer instance
 */
function useWavesurferInstance(
	containerRef: MaybeRefOrGetter<HTMLDivElement | null>,
	options: Omit<DeepMaybeRefOrGetterWavesurferOptions, 'container'>,
): ComputedRef<WaveSurfer | null> {
	const wavesurfer = shallowRef<WaveSurfer | null>(null);

	// Create a wavesurfer instance
	watchEffect((onCleanup) => {
		const container = toValue(containerRef);
		if (!container) return;

		const optionValues = options ? mapValues(options, (value) => toValue(value)) : {};

		const ws = WaveSurfer.create({
			...optionValues,
			container,
		});

		wavesurfer.value = ws;

		onCleanup(() => {
			ws.destroy();
		});
	});

	return computed(() => wavesurfer.value);
}

/**
 * Use wavesurfer state
 */
function useWavesurferState(wavesurfer: Ref<WaveSurfer | null>): {
	isReady: Readonly<ShallowRef<boolean>>;
	isPlaying: Readonly<ShallowRef<boolean>>;
	hasFinished: Readonly<ShallowRef<boolean>>;
	currentTime: Readonly<ShallowRef<number>>;
} {
	const state = reactive({
		isReady: false,
		isPlaying: false,
		hasFinished: false,
		currentTime: 0,
	});

	watchEffect((onCleanup) => {
		const ws = wavesurfer.value;
		if (!ws) return;

		const unsubscribeFns = [
			ws.on('load', () => {
				state.isReady = false;
				state.isPlaying = false;
				state.currentTime = 0;
			}),

			ws.on('ready', () => {
				state.isReady = true;
				state.isPlaying = false;
				state.hasFinished = false;
				state.currentTime = 0;
			}),

			ws.on('finish', () => {
				state.hasFinished = true;
			}),

			ws.on('play', () => {
				state.isPlaying = true;
			}),

			ws.on('pause', () => {
				state.isPlaying = false;
			}),

			ws.on('timeupdate', () => {
				state.currentTime = ws.getCurrentTime();
			}),

			ws.on('destroy', () => {
				state.isReady = false;
				state.isPlaying = false;
			}),
		];

		onCleanup(() => {
			for (const fn of unsubscribeFns) fn();
		});
	});

	return {
		isReady: computed(() => state.isReady),
		isPlaying: computed(() => state.isPlaying),
		hasFinished: computed(() => state.hasFinished),
		currentTime: computed(() => state.currentTime),
	};
}

const EVENT_PROP_RE = /^on([A-Z])/;
const isEventProp = (key: string) => EVENT_PROP_RE.test(key);
const getEventName = (key: string) => key.replace(EVENT_PROP_RE, (_, $1) => $1.toLowerCase()) as keyof WaveSurferEvents;

/**
 * Parse props into wavesurfer options and events
 */
function useWavesurferProps(props: WavesurferProps): [DeepMaybeRefOrGetterWavesurferOptions, OnWavesurferEvents] {
	// Props starting with `on` are wavesurfer events, e.g. `onReady`
	// The rest of the props are wavesurfer options
	const allOptions = { ...props };
	const allEvents = { ...props };

	for (const key in allOptions) {
		if (isEventProp(key)) {
			delete allOptions[key as keyof WavesurferProps];
		}
		else {
			delete allEvents[key as keyof WavesurferProps];
		}
	}

	return [allOptions, allEvents];
}

/**
 * Subscribe to wavesurfer events
 */
function useWavesurferEvents(wavesurfer: MaybeRefOrGetter<WaveSurfer | null>, events: MaybeRefOrGetter<OnWavesurferEvents>) {
	// Subscribe to events
	watchEffect((onCleanup) => {
		const ws = toValue(wavesurfer);
		if (!ws) return;

		const eventEntries = Object.entries(toValue(events));
		if (eventEntries.length === 0) return;

		const unsubscribeFns = eventEntries.map(([name, handler]) => {
			const event = getEventName(name);
			return ws.on(event, (...args) =>
				(handler as WavesurferEventHandler<WaveSurferEvents[typeof event]>)(ws, ...args));
		});

		onCleanup(() => {
			for (const fn of unsubscribeFns) fn();
		});
	});
}

/**
 * Vue composable for wavesurfer.js
 *
 * ```
 * import { useWavesurfer } from './use-wavesurfer'
 *
 * const containerRef = ref<HTMLDivElement>()
 *
 * const { wavesurfer, isReady, isPlaying, hasFinished, currentTime } = useWavesurfer({
 *   container: containerRef,
 *   url: '/my-server/audio.ogg',
 *   waveColor: 'purple',
 *   height: 100,
 * })
 * ```
 *
 * @public
 */
export function useWavesurfer(options: DeepMaybeRefOrGetterWavesurferOptions): ReturnType<typeof useWavesurferState> & {
	wavesurfer: ReturnType<typeof useWavesurferInstance>;
} {
	const [wavesurferOptions, events] = useWavesurferProps(options);
	const wavesurfer = useWavesurferInstance(options.container, wavesurferOptions);
	const state = useWavesurferState(wavesurfer);
	useWavesurferEvents(wavesurfer, events);

	return computed(() => ({ ...state, wavesurfer })).value;
}
